// useFindExisting.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Utilities
import { ADD_MODAL_TABS } from 'components/addmodal/utilities/constants';

// Services
import { searchBaseNodes } from 'services/search/BaseNodeSearchService';
import { tokenizeSearchQuery } from 'services/search/SearchIndexService';
import { listAdjacentNodeKeys, upsertEdge } from 'services/nodes/NodeEdgeService';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';

/**
 * useFindExisting
 * 
 * Custom hook for managing the "Find Existing" functionality in the AddModal component.
 * It handles searching for existing nodes based on a search term, filtering results,
 * and attaching a selected node to the parent node.
 * 
 * @param {boolean} isOpen - Whether the AddModal is open.
 * @param {boolean} showFind - Whether to show the "Find Existing" tab.
 * @param {string} selectedTab - The currently selected tab key.
 * @param {string} findTabValue - The tab key for the "Find Existing" tab.
 * @param {string} baseKey - The key of the current base for filtering search results.
 * @param {string|null} anchorNodeKey - The key of the anchor node to which existing nodes will be attached.
 * @param {object|null} anchorNode - Optional full anchor node (for hierarchical edge typing).
 * @param {function} onClose - Function to call when the modal is closed.
 * @param {function} onAttached - Function to call after an existing node is attached.
 * 
 * @returns {Object} An object containing state and handlers for the "Find Existing" functionality.
 */
export function useFindExisting({
  enableSelectionWithoutAnchor = false,
  isOpen,
  showFind,
  selectedTab,
  findTabValue = ADD_MODAL_TABS.FIND,
  baseKey,
	anchorNodeKey,
  anchorNode = null,
  onClose,
  onAttached,
}) {

  // ----- STATE ----- //

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [attachingNodeKey, setAttachingNodeKey] = useState('');
  const [existingRelatedKeys, setExistingRelatedKeys] = useState([]);

  // ----- MEMOIZED VALUES ----- //

  // Normalizes the search term into tokens for filtering
  const normalizedSearchTokens = useMemo(() => {
    return tokenizeSearchQuery(searchTerm);
  }, [searchTerm]);

  // Determines the query token (the last token) for triggering searches
  const queryToken = normalizedSearchTokens.length
    ? normalizedSearchTokens[normalizedSearchTokens.length - 1]
    : '';

  // ----- EFFECTS ----- //
  
  // Resets search state when the modal is opened or closed
  useEffect(() => {
    if (!isOpen) return;

    setSearchTerm('');
    setSearchResults([]);
    setSearching(false);
    setSearchError('');
    setAttachingNodeKey('');
    setExistingRelatedKeys([]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !anchorNodeKey) {
      setExistingRelatedKeys([]);
      return undefined;
    }

    let cancelled = false;

    const loadExistingRelatedKeys = async () => {
      try {
        const relatedKeys = await listAdjacentNodeKeys(anchorNodeKey, { baseKey });
        if (cancelled) return;
        setExistingRelatedKeys(Array.isArray(relatedKeys) ? relatedKeys : []);
      } catch (error) {
        if (cancelled) return;
        setExistingRelatedKeys([]);
      }
    };

    loadExistingRelatedKeys();

    return () => {
      cancelled = true;
    };
  }, [isOpen, anchorNodeKey, baseKey]);

  // Performs the search when the query token changes
  useEffect(() => {

    // Only perform search if the "Find Existing" tab is active
    if (!showFind || selectedTab !== findTabValue) return undefined;

    // If there's no query token, clear results and return early
    if (!queryToken) {
      setSearchResults([]);
      setSearching(false);
      setSearchError('');
      return undefined;
    }

    // Set up a cancellation mechanism in case the component unmounts or the search term changes
    let cancelled = false;

    // Start searching
    setSearching(true);
    setSearchError('');

    // Use a debounced search to avoid firing too many requests while the user is typing
    const handle = setTimeout(async () => {
      try {

        const rows = await searchBaseNodes({
          rawQuery: searchTerm,
          baseKey,
          max: 50,
        });

        // If the search was cancelled while waiting for results, do nothing
        if (cancelled) return;

        // Filter results and remove invalid/self rows.
        const filtered = (rows || []).filter((row) => {

          // Only include rows that have a key
          if (!row?.key) return false;

          if (anchorNodeKey && row.key === anchorNodeKey) return false;
          if (anchorNodeKey && existingRelatedKeys.includes(row.key)) return false;
          return true;
        });

        // Sort the filtered results alphabetically by title before setting state
        filtered.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || '')));

        // Update state with the filtered search results
        setSearchResults(filtered);
      } catch (error) {
        // If there was an error and the search wasn't cancelled, set the error state
        if (cancelled) return;
        setSearchResults([]);
        setSearchError(error?.message || 'Failed to search nodes.');
      } finally {
        // Set searching to false if the search wasn't cancelled
        if (!cancelled) setSearching(false);
      }
    }, 220);

    // Cleanup function to cancel the search if the component unmounts or if the search 
    // term changes before the timeout completes
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    showFind,
    selectedTab,
    findTabValue,
    queryToken,
    searchTerm,
    baseKey,
    anchorNodeKey,
    existingRelatedKeys,
  ]);

  /**
   * Handles attaching an existing node to the anchor node.
   * After successfully attaching, it closes the modal.
   * 
   * @param {Object} node - The node to attach, which should have a key field.
   */
  const handleAttachExisting = useCallback(async (node) => {

    if (!node?.key) return;

    if (enableSelectionWithoutAnchor && !anchorNodeKey) {
      onAttached?.(node);
      onClose?.();
      return;
    }

    // Validate that the node has a key and that the anchor node key is valid and not the same as 
    // the node's key
	if (!anchorNodeKey || node.key === anchorNodeKey) return;

    // Set the attachingNodeKey to provide feedback in the UI that this node is being attached
    setAttachingNodeKey(node.key);

    try {
    const result = await upsertEdge(
      anchorNodeKey,
      node.key,
      buildHierarchyEdgeOptions({
        parentNode: anchorNode || { key: anchorNodeKey },
        childNode: node,
        baseKey,
      }),
    );

    if (result?.status === 'noop') {
      toast.info('That node is already related.');
      setExistingRelatedKeys((prev) => (
        prev.includes(node.key) ? prev : [...prev, node.key]
      ));
      return;
    }

    setExistingRelatedKeys((prev) => (
      prev.includes(node.key) ? prev : [...prev, node.key]
    ));
      onAttached?.(node);
      onClose?.();
    } catch (error) {
      console.warn('[AddModal] failed to attach existing node:', error);
    } finally {
      setAttachingNodeKey('');
    }
	}, [anchorNode, anchorNodeKey, enableSelectionWithoutAnchor, onAttached, onClose, baseKey]);

  // Determines if there are no results to show based on the search state
  const noResults = Boolean(queryToken) && !searching && !searchResults.length && !searchError;

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    searching,
    searchError,
    attachingNodeKey,
    handleAttachExisting,
    noResults,
  };
  
}
